import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getSocket, connectSocket } from '../services/socketService';
import './FriendsPanel.css';

const FriendsPanel = ({ isOpen, onClose, user, onNavigateToMultiplayer, onShowPublicProfile }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [invite, setInvite] = useState(null);
  const [sentRequests, setSentRequests] = useState([]);

  const searchTimeoutRef = useRef(null);
  const prevOpenRef = useRef(false);

  // Fetch friends list
  const fetchFriends = useCallback(async () => {
    try {
      const response = await fetch('/api/friends', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFriends(data);
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  }, []);

  // Fetch pending requests
  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch('/api/friends/requests', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
        setPendingCount(data.length);
      }
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  }, []);

  // Fetch sent requests to know which users already have pending requests
  const fetchSentRequests = useCallback(async () => {
    try {
      const response = await fetch('/api/friends/sent', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSentRequests(data);
      }
    } catch (error) {
      console.error('Error fetching sent requests:', error);
    }
  }, []);

  // On open, fetch data
  useEffect(() => {
    if (isOpen && !prevOpenRef.current && user) {
      fetchFriends();
      fetchRequests();
      fetchSentRequests();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, user, fetchFriends, fetchRequests, fetchSentRequests]);

  // Socket listeners
  useEffect(() => {
    if (!user) return;

    const socket = connectSocket();

    const onRequestReceived = () => {
      setPendingCount(prev => prev + 1);
      fetchRequests();
    };

    const onInviteReceived = (data) => {
      setInvite(data);
    };

    socket.on('friend:request-received', onRequestReceived);
    socket.on('friend:invite-received', onInviteReceived);

    return () => {
      socket.off('friend:request-received', onRequestReceived);
      socket.off('friend:invite-received', onInviteReceived);
    };
  }, [user, fetchRequests]);

  // Search debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/users/search?q=${encodeURIComponent(searchQuery.trim())}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleAccept = async (requestId) => {
    try {
      const response = await fetch(`/api/friends/${requestId}/accept`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        fetchRequests();
        fetchFriends();
      }
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };

  const handleReject = async (requestId) => {
    try {
      const response = await fetch(`/api/friends/${requestId}/reject`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        fetchRequests();
      }
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    }
  };

  const handleRemoveFriend = async (friendId) => {
    try {
      const response = await fetch(`/api/friends/${friendId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setFriends(prev => prev.filter(f => f.id !== friendId));
      }
    } catch (error) {
      console.error('Error removing friend:', error);
    }
  };

  const handleSendRequest = async (userId) => {
    try {
      const response = await fetch('/api/friends/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId })
      });
      if (response.ok) {
        fetchSentRequests();
        // Update search results to reflect sent state
        setSearchResults(prev => prev.map(u =>
          u.id === userId ? { ...u, requestSent: true } : u
        ));
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
    }
  };

  const handleAcceptInvite = (lobbyCode) => {
    setInvite(null);
    if (onNavigateToMultiplayer) {
      onNavigateToMultiplayer(lobbyCode);
    }
  };

  // Check if user is already a friend or has a pending request
  const isAlreadyFriend = (userId) => {
    return friends.some(f => f.friend_id === userId);
  };

  const hasSentRequest = (userId) => {
    return sentRequests.some(r => r.friend_id === userId) ||
           searchResults.some(u => u.id === userId && u.requestSent);
  };

  return (
    <div className={`friends-panel ${isOpen ? 'open' : ''}`}>
      <div className="friends-panel-overlay" onClick={onClose} />
      <div className="friends-panel-content">
        <div className="fp-header">
          <h3>{t('friends.title', 'Friends')}</h3>
          <button className="fp-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="fp-tabs">
          <button
            className={activeTab === 'friends' ? 'active' : ''}
            onClick={() => setActiveTab('friends')}
          >
            {t('friends.friendsTab', 'Friends')}
          </button>
          <button
            className={activeTab === 'requests' ? 'active' : ''}
            onClick={() => setActiveTab('requests')}
          >
            {t('friends.requestsTab', 'Requests')}
            {pendingCount > 0 && <span className="badge">{pendingCount}</span>}
          </button>
          <button
            className={activeTab === 'search' ? 'active' : ''}
            onClick={() => setActiveTab('search')}
          >
            {t('friends.searchTab', 'Search')}
          </button>
        </div>

        <div className="fp-content">
          {activeTab === 'friends' && (
            <div className="fp-friends-list">
              {friends.map(friend => (
                <div key={friend.id} className="fp-friend-item">
                  <div
                    className="fp-friend-clickable"
                    onClick={() => onShowPublicProfile && onShowPublicProfile(friend.battlenet_tag)}
                    title={t('friends.viewProfile', 'View profile')}
                  >
                    <img
                      src={friend.avatar_url || '/images/default-avatar.svg'}
                      alt=""
                      className="fp-avatar"
                    />
                    <div className="fp-friend-info">
                      <span className="fp-friend-name">{friend.display_name || friend.battlenet_tag}</span>
                      <span className={`fp-status ${friend.streamerMode ? 'streamer' : friend.online ? 'online' : 'offline'}`}>
                        <span className="fp-status-dot"></span>
                        {friend.streamerMode
                          ? t('friends.streamerMode', 'Streamer Mode')
                          : friend.online
                            ? t('friends.online', 'Online')
                            : t('friends.offline', 'Offline')
                        }
                      </span>
                    </div>
                  </div>
                  <button
                    className="fp-action-btn remove"
                    onClick={() => handleRemoveFriend(friend.id)}
                    title={t('friends.remove', 'Remove')}
                  >
                    <i className="fas fa-user-minus"></i>
                  </button>
                </div>
              ))}
              {friends.length === 0 && !loading && (
                <p className="fp-empty">{t('friends.noFriends', 'No friends yet. Search for players!')}</p>
              )}
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="fp-requests-list">
              {requests.map(req => (
                <div key={req.id} className="fp-request-item">
                  <img
                    src={req.avatar_url || '/images/default-avatar.svg'}
                    alt=""
                    className="fp-avatar"
                  />
                  <span className="fp-friend-name">{req.display_name || req.battlenet_tag}</span>
                  <div className="fp-request-actions">
                    <button className="fp-btn accept" onClick={() => handleAccept(req.id)}>
                      <i className="fas fa-check"></i>
                    </button>
                    <button className="fp-btn reject" onClick={() => handleReject(req.id)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              ))}
              {requests.length === 0 && !loading && (
                <p className="fp-empty">{t('friends.noRequests', 'No pending requests')}</p>
              )}
            </div>
          )}

          {activeTab === 'search' && (
            <div className="fp-search">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('friends.searchPlaceholder', 'Search by BattleTag...')}
                className="fp-search-input"
              />
              <div className="fp-search-results">
                {loading && (
                  <p className="fp-loading">{t('friends.searching', 'Searching...')}</p>
                )}
                {searchResults.map(u => (
                  <div key={u.id} className="fp-search-item">
                    <img
                      src={u.avatar_url || '/images/default-avatar.svg'}
                      alt=""
                      className="fp-avatar"
                    />
                    <span className="fp-friend-name">{u.display_name || u.battlenet_tag}</span>
                    {isAlreadyFriend(u.id) ? (
                      <span className="fp-already-friend">
                        <i className="fas fa-check"></i>
                      </span>
                    ) : hasSentRequest(u.id) ? (
                      <span className="fp-request-sent">
                        <i className="fas fa-clock"></i>
                      </span>
                    ) : (
                      <button className="fp-btn add" onClick={() => handleSendRequest(u.id)}>
                        <i className="fas fa-user-plus"></i>
                      </button>
                    )}
                  </div>
                ))}
                {!loading && searchQuery.trim() && searchResults.length === 0 && (
                  <p className="fp-empty">{t('friends.noResults', 'No users found')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Invite notification overlay */}
        {invite && (
          <div className="fp-invite-toast">
            <p>
              <i className="fas fa-gamepad"></i>
              {t('friends.inviteMessage', '{{name}} invited you to play!', { name: invite.from.nickname })}
            </p>
            <div className="fp-invite-actions">
              <button className="fp-btn accept" onClick={() => handleAcceptInvite(invite.lobbyCode)}>
                {t('friends.join', 'Join')}
              </button>
              <button className="fp-btn reject" onClick={() => setInvite(null)}>
                {t('friends.dismiss', 'Dismiss')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsPanel;
